import React from "react";
import { Box, Stack } from "@mui/material";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import SectionFrame from "../SectionFrame";
import { EditableButton, EditableText } from "../InlineEditable";
import EditableImage from "../EditableImage";
import { resolveLandingAssetUrl } from "../../../../utils/landingAssets";

export default function HeroSection({ form, active, device, onSelect, onCommit, onUploadImage, onRemoveImage, disabled }) {
  const isMobile = device === "mobile";
  const primary = form.theme.primaryColor || "#F35A3A";
  const secondary = form.theme.secondaryColor || "#19242F";
  const firstSlide = form.hero.slides?.[0];
  const imagePath = firstSlide?.url ? "hero.slides.0.url" : "hero.imageUrl";
  const image = firstSlide?.url || form.hero.imageUrl || "";
  const resolvedImage = resolveLandingAssetUrl(image);
  const title = firstSlide?.title || form.hero.title;
  const subtitle = firstSlide?.subtitle || form.hero.subtitle;
  const phrase = form.hero.rotatingPhrases?.[0] || "";
  const enabled = form.sections.showHero !== false;

  return (
    <SectionFrame
      id="hero"
      label="Hero"
      active={active}
      enabled={enabled}
      onSelect={onSelect}
      onToggle={(next) => onCommit("sections.showHero", next)}
    >
      <Box
        sx={{
          position: "relative",
          minHeight: isMobile ? 610 : 720,
          height: isMobile ? "78svh" : "min(82svh, 820px)",
          overflow: "hidden",
          bgcolor: secondary,
        }}
      >
        {resolvedImage ? (
          <Box
            component="img"
            src={resolvedImage}
            alt={firstSlide?.alt || title || form.business.name || "Hero"}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: firstSlide?.position || form.hero.imagePosition || "center",
            }}
          />
        ) : (
          <Box sx={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${secondary}, ${primary})` }} />
        )}
        <Box sx={{ position: "absolute", inset: 0, background: form.hero.overlay || "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.75))" }} />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            px: isMobile ? 2.2 : 5,
            pb: isMobile ? 6 : 8,
            pt: 13,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 980, textAlign: "center", color: "#fff" }}>
            <EditableText
              value={form.hero.eyebrow}
              fallback="Una propuesta pensada para tus clientes"
              disabled={disabled}
              onSave={(value) => onCommit("hero.eyebrow", value)}
              sx={{ display: "block", color: "rgba(255,255,255,.78)", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 850, textShadow: "0 2px 10px rgba(0,0,0,.45)" }}
            />
            <Box sx={{ mt: 0.7 }}>
              <EditableText
                value={title}
                fallback={form.business.name || "Título principal"}
                disabled={disabled}
                multiline
                minRows={2}
                onSave={(value) => onCommit(firstSlide?.title ? "hero.slides.0.title" : "hero.title", value)}
                sx={{
                  color: phrase ? primary : "#fff",
                  fontWeight: 950,
                  fontSize: isMobile ? "clamp(2.8rem, 13vw, 4.6rem)" : "clamp(4rem, 7vw, 7.2rem)",
                  lineHeight: 0.98,
                  letterSpacing: "-.055em",
                  textShadow: "0 4px 24px rgba(0,0,0,.62)",
                }}
                inputSx={{ fontSize: isMobile ? 38 : 58, fontWeight: 950, textAlign: "center" }}
              />
            </Box>
            {phrase ? (
              <Box sx={{ mt: 0.5 }}>
                <EditableText
                  value={phrase}
                  fallback="Frase rotativa"
                  disabled={disabled}
                  onSave={(value) => onCommit("hero.rotatingPhrases.0", value)}
                  sx={{ color: "#fff", fontWeight: 900, fontSize: isMobile ? 32 : 52, textShadow: "0 4px 22px rgba(0,0,0,.68)" }}
                />
              </Box>
            ) : null}
            <Box sx={{ mt: 1.6, mx: "auto", maxWidth: 760 }}>
              <EditableText
                value={subtitle || form.business.shortDescription}
                fallback="Contá de manera simple qué ofrecés y por qué deberían elegirte."
                disabled={disabled}
                multiline
                minRows={3}
                onSave={(value) => onCommit(firstSlide?.subtitle ? "hero.slides.0.subtitle" : "hero.subtitle", value)}
                sx={{ color: "rgba(255,255,255,.88)", fontSize: isMobile ? 16 : 19, lineHeight: 1.65, textShadow: "0 2px 12px rgba(0,0,0,.55)" }}
              />
            </Box>
            <Stack direction={isMobile ? "column" : "row"} spacing={1.4} justifyContent="center" sx={{ mt: 3.2 }}>
              <Stack direction="row" spacing={0.6} justifyContent="center" alignItems="center">
                <ExploreRoundedIcon />
                <EditableButton
                  value={form.hero.primaryCtaLabel}
                  fallback="Consultar"
                  disabled={disabled}
                  onSave={(value) => onCommit("hero.primaryCtaLabel", value)}
                  sx={{ minWidth: isMobile ? "100%" : 190, bgcolor: primary, color: "#fff", borderRadius: 999, px: 3.5, py: 1.2, fontWeight: 950, boxShadow: "0 10px 28px rgba(0,0,0,.28)" }}
                />
              </Stack>
              <Stack direction="row" spacing={0.6} justifyContent="center" alignItems="center">
                <StorefrontRoundedIcon />
                <EditableButton
                  value={form.hero.secondaryCtaLabel}
                  fallback="Ver productos"
                  disabled={disabled}
                  variant="outlined"
                  onSave={(value) => onCommit("hero.secondaryCtaLabel", value)}
                  sx={{ minWidth: isMobile ? "100%" : 190, bgcolor: "rgba(255,255,255,.08)", color: "#fff", borderColor: "rgba(255,255,255,.7)", borderRadius: 999, px: 3.5, py: 1.15, fontWeight: 900, backdropFilter: "blur(10px)" }}
                />
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ position: "absolute", zIndex: 12, left: 14, bottom: 14, width: isMobile ? 155 : 220 }}>
          <EditableImage
            value={image}
            label="Fondo del hero"
            height={isMobile ? 86 : 104}
            disabled={disabled}
            onUpload={(file) => onUploadImage(imagePath, file, { sectionKey: "hero", assetKey: imagePath.includes("slides") ? "slide-1" : "background" })}
            onRemove={() => onRemoveImage(imagePath)}
            sx={{ bgcolor: "rgba(255,255,255,.12)", borderColor: "rgba(255,255,255,.5)" }}
          />
        </Box>
      </Box>
    </SectionFrame>
  );
}
